<script setup lang="ts">
  import { Form } from 'vee-validate'
  import * as yup from 'yup'
  import type { CandidateGender } from '@profilpublic/api-contract/candidate'
  import type {
    ApplyCareerSiteFragment,
    ApplyDocuments,
    ApplyJobFragment,
    ApplyProfile,
    CvExtraction,
    CvExtractionBucket,
    SelectOption,
    SpecialisationGroup
  } from '~/types/apply'
  import 'yup-phone-lite'
  import policySchema from '~/utils/schema/policy-schema'

  const props = withDefaults(
    defineProps<{
      extraction?: CvExtraction | null // normalizeExtraction output (null = manual)
      documents?: ApplyDocuments | null
      job?: ApplyJobFragment | null
      careerSite?: ApplyCareerSiteFragment
      submitting?: boolean // career: this step submits directly (no account step)
      resumeDeposit?: boolean // /depot-cv : pas d'offre, on dépose un profil candidat
      /** ATS manual add: a recruiter fills in someone else's profile. */
      recruiter?: boolean
      /** /inscription: the profile completes an account creation. */
      signup?: boolean
      title?: string
      submitLabel?: string
    }>(),
    {
      extraction: null,
      documents: null,
      job: null,
      careerSite: null,
      submitting: false,
      resumeDeposit: false,
      recruiter: false,
      signup: false,
      title: undefined,
      submitLabel: undefined
    }
  )

  const emit = defineEmits<{
    submit: [values: ApplyProfile]
    back: []
  }>()

  const theme = useCareerTheme()
  const primaryStyle = computed(() => (theme.value.primary ? { color: theme.value.primary } : undefined))
  const primaryButtonStyle = computed(() =>
    theme.value.primary
      ? {
          backgroundColor: theme.value.primary,
          borderColor: theme.value.primary,
          color: theme.value.text || theme.value.primary,
          '--button-hover-color': theme.value.primary
        }
      : undefined
  )
  const cancelButtonStyle = computed(() => (theme.value.primary ? { color: theme.value.text || undefined } : undefined))

  type ReferenceRow = { id: number; name: string; sector?: number | { id?: number; data?: { id?: number } } | null }

  const toOptions = (rows?: ReferenceRow[] | null): SelectOption[] => (rows || []).map(r => ({ label: r.name, value: r.id }))
  const relationId = (relation?: number | { id?: number; data?: { id?: number } } | null): number | undefined => {
    if (typeof relation === 'number') return relation
    return relation?.id ?? relation?.data?.id
  }
  const cityName = (city: unknown) => (typeof city === 'object' && city !== null ? (city as { name?: string }).name || '' : (city as string) || '')
  const citySlug = (city: unknown) => (typeof city === 'object' && city !== null ? (city as { slug?: string }).slug || null : null)

  type ReferenceOptions = {
    sectors: SelectOption[]
    subSectors: SelectOption[]
    expertises: SelectOption[]
    drivingLicenses: SelectOption[]
    languages: SelectOption[]
    exams: SelectOption[]
    specialisationGroups: SpecialisationGroup[]
  }

  const refs = reactive<{ data: ReferenceOptions | null }>({ data: null })
  onMounted(async () => {
    const [sectors, subSectors, expertises, drivingLicenses, languages, exams] = await Promise.all([
      findAll('sectors', { fields: ['id', 'name'], sort: { name: 'asc' } }),
      findAll('sub-sectors', { fields: ['id', 'name'], populate: { sector: { fields: ['id'] } }, sort: { name: 'asc' } }),
      findAll('expertises', { fields: ['id', 'name'], sort: { name: 'asc' } }),
      findAll('driving-licenses', { fields: ['id', 'name'], sort: { name: 'asc' } }),
      findAll('languages', { fields: ['id', 'name'], sort: { name: 'asc' } }),
      findAll('exams', { fields: ['id', 'name'], sort: { name: 'asc' } })
    ])

    refs.data = {
      sectors: toOptions(sectors),
      subSectors: toOptions(subSectors),
      expertises: toOptions(expertises),
      drivingLicenses: toOptions(drivingLicenses),
      languages: toOptions(languages),
      exams: toOptions(exams),
      specialisationGroups: (sectors || []).map(s => ({
        sector: { label: s.name, value: `sector:${s.id}` },
        subSectors: (subSectors || []).filter(ss => relationId(ss.sector) === s.id).map(ss => ({ label: ss.name, value: `subSector:${ss.id}` }))
      }))
    }
  })

  const CIVILITY_OPTIONS = [
    { label: 'Monsieur', value: 'male' },
    { label: 'Madame', value: 'female' },
    { label: 'Autre', value: 'other' }
  ]
  const EXPERIENCE_OPTIONS = [
    { label: '0-3 ans', value: 'beginner' },
    { label: '3-5 ans', value: 'junior' },
    { label: '5-10 ans', value: 'confirmed' },
    { label: '+10 ans', value: 'senior' }
  ]
  const YES_NO = [
    { label: 'Non', value: false },
    { label: 'Oui', value: true }
  ]
  const YES_NO_DECLINE = [...YES_NO, { label: 'Je ne souhaite pas répondre', value: null }]
  const YES_NO_UNKNOWN = [...YES_NO, { label: 'Non renseigné', value: null }]
  const AVATAR_TYPES = ['image/gif', 'image/jpg', 'image/jpeg', 'image/png', 'image/webp'].join(',')

  const civilityToGender = (c?: string | null): CandidateGender | null =>
    c === 'Madame' ? 'female' : c === 'Monsieur' ? 'male' : c === 'Autre' ? 'other' : null
  const isPublicToOfficial = (v?: string | null) => (v === 'yes' ? true : v === 'no' ? false : null)
  const mergeIds = (...groups: Array<Array<string | number> | undefined>): number[] => [
    ...new Set(
      groups
        .flatMap(group => group || [])
        .map(Number)
        .filter(id => Number.isInteger(id) && id > 0)
    )
  ]

  // Map the AI's matched DB ids (or legacy canonical names) onto option ids.
  const resolveIds = (names?: string[] | null, options?: SelectOption[]) => {
    const wanted = new Set((names || []).map(n => String(n).toLowerCase()))
    return (options || []).filter(o => wanted.has(String(o.label).toLowerCase())).map(o => o.value)
  }
  const resolveIdsById = (ids?: number[] | null, options?: SelectOption[]) => {
    const wanted = new Set((ids || []).map(id => Number(id)).filter(Number.isFinite))
    return (options || []).filter(o => wanted.has(Number(o.value))).map(o => o.value)
  }
  const resolveId = (name?: string | null, options?: SelectOption[]) => {
    if (!name) return null
    const hit = (options || []).find(o => String(o.label).toLowerCase() === String(name).toLowerCase())
    return hit ? hit.value : null
  }
  const resolveIdById = (id?: number | null, options?: SelectOption[]) => {
    const wanted = Number(id)
    if (!Number.isFinite(wanted)) return null
    const hit = (options || []).find(o => Number(o.value) === wanted)
    return hit ? hit.value : null
  }
  const resolveExtractionIds = (bucket?: CvExtractionBucket, options?: SelectOption[]) => {
    const byId = resolveIdsById(bucket?.matchedIds, options)
    return byId.length ? byId : resolveIds(bucket?.matched, options)
  }

  const prefill = computed(() => {
    const e: CvExtraction = props.extraction || {}
    const o: Partial<ReferenceOptions> = refs.data || {}
    const extractedSectors = resolveExtractionIds(e.domains, o.sectors)
    const extractedSubSectors = resolveExtractionIds(e.subSectors, o.subSectors)
    const sectors = mergeIds(extractedSectors)
    const subSectors = mergeIds(extractedSubSectors)
    const extractedExam = resolveIdById(e.examId, o.exams) || resolveId(e.exam, o.exams)
    return {
      gender: civilityToGender(e.civility) || 'male',
      official: isPublicToOfficial(e.isPublicWorker),
      experience: e.experience ?? null,
      exam: extractedExam,
      specialisation: [...sectors.map(id => `sector:${id}`), ...subSectors.map(id => `subSector:${id}`)],
      expertises: mergeIds(resolveExtractionIds(e.expertises, o.expertises)),
      drivingLicenses: mergeIds(resolveExtractionIds(e.drivingLicenses, o.drivingLicenses)),
      languages: mergeIds(resolveExtractionIds(e.languages, o.languages))
    }
  })

  // Prefill this form from the CV analysis only.
  const initialValues = computed(() => {
    const e: CvExtraction = props.extraction || {}
    const p = prefill.value
    return {
      avatar: null,
      gender: p.gender,
      firstName: e.firstName || '',
      lastName: e.lastName || '',
      email: e.email || '',
      phone: e.phone || '',
      linkedin: e.linkedin || '',
      location: e.location || '',
      summary: e.summary || '',
      official: p.official,
      exam: p.exam,
      experience: p.experience,
      specialisation: p.specialisation,
      expertises: p.expertises,
      drivingLicenses: p.drivingLicenses,
      languages: p.languages,
      internalCandidate: null,
      rqth: null,
      policy: false
    }
  })

  const schema = yup.object({
    firstName: yup.string().required('Prénom requis'),
    lastName: yup.string().required('Nom requis'),
    // Recruiters often only have the candidate's email.
    phone: props.recruiter
      ? yup
          .string()
          .nullable()
          .transform(value => (value === '' ? null : value))
          .label('Téléphone')
      : yup.string().phone('FR', '${label} invalide').required('${label} requis').label('Téléphone'),
    email: yup.string().email().required('Email requis'),
    specialisation: props.recruiter ? yup.array() : yup.array().min(1, 'Spécialisation requise'),
    avatar: yupImage({ minWidth: 150, minHeight: 150 }).nullable().label('Photo'),
    ...(props.recruiter ? {} : { policy: policySchema() })
  })

  const onSubmit = (values: Record<string, unknown> & { specialisation?: Array<string | number> }) => {
    const parsed = (values.specialisation || []).map(v => String(v).split(':'))
    const sectors = parsed.filter(([t]) => t === 'sector').map(([, id]) => Number(id))
    const subSectors = parsed.filter(([t]) => t === 'subSector').map(([, id]) => Number(id))
    const { specialisation, ...rest } = schema.cast(values, { stripUnknown: false })
    emit('submit', {
      ...rest,
      location: cityName(values.location),
      locationSlug: citySlug(values.location),
      sectors,
      subSectors
    } as ApplyProfile)
  }

  const onInvalidSubmit = async () => {
    await nextTick()
    document.querySelector('.text-red-500, .form-input.error, .form-select.error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const title = computed(() => props.title || (props.resumeDeposit ? 'Je dépose mon CV' : 'Je postule'))
  const submitLabel = computed(() => props.submitLabel || (props.resumeDeposit || props.signup ? 'Envoyer' : 'Postuler'))
  // The ATS asks Candidature interne / RQTH before the analysis; sign-up does not ask them.
  const askApplicationTags = computed(() => !props.recruiter && !props.signup)
  // A CV deposit has no recruiter recipient.
  const policyPurpose = computed(() => (props.resumeDeposit ? 'de la mise en relation avec des recruteurs publics' : undefined))
</script>

<template>
  <div class="mx-auto w-full max-w-2xl">
    <h1 class="text-center font-special text-3xl font-bold" :style="primaryStyle">{{ title }}</h1>

    <Loading class="mt-8" v-if="!refs.data" :color="theme.primary || undefined" />

    <Form
      class="mt-6"
      v-else
      v-slot="{ errors, values, isSubmitting, setFieldValue }"
      :initial-values="initialValues"
      :validation-schema="schema"
      @submit="onSubmit"
      @invalid-submit="onInvalidSubmit"
    >
      <div class="space-y-4">
        <div class="flex items-center justify-center gap-4 sm:flex-row sm:items-start">
          <FormField
            class="shrink-0"
            id="avatar"
            name="avatar"
            type="image"
            label="Charger une photo"
            :accept="AVATAR_TYPES"
            :error="errors.avatar"
          />

          <div class="-m-2 flex flex-1 flex-wrap">
            <FormField
              class="basis-full p-2 sm:basis-1/3"
              id="gender"
              name="gender"
              type="select"
              custom
              placeholder="Civilité"
              :options="CIVILITY_OPTIONS"
              :value="prefill.gender"
              :error="errors.gender"
              @select="v => setFieldValue('gender', v)"
            />
            <FormField class="basis-full p-2 sm:basis-1/3" id="firstName" name="firstName" placeholder="Prénom" required :error="errors.firstName" />
            <FormField class="basis-full p-2 sm:basis-1/3" id="lastName" name="lastName" placeholder="Nom" required :error="errors.lastName" />
            <div class="basis-full p-2">
              <FormCitySearch
                id="location"
                name="location"
                :model-value="values.location"
                placeholder="Ville"
                :allow-radius="false"
                :allow-custom="false"
                :return-object="true"
                resolve-initial
                :show-icon="false"
                show-postal-code
                :error="errors.location"
                @update:model-value="v => setFieldValue('location', v)"
              />
            </div>
            <FormField class="basis-full p-2 sm:basis-1/3" id="phone" name="phone" type="phone" placeholder="Téléphone" :error="errors.phone" />
            <FormField class="basis-full p-2 sm:basis-1/3" id="email" name="email" type="email" placeholder="Email" required :error="errors.email" />
            <FormField
              class="basis-full p-2 sm:basis-1/3"
              id="linkedin"
              name="linkedin"
              type="url"
              placeholder="URL Linkedin"
              :error="errors.linkedin"
            />
          </div>
        </div>

        <div>
          <FormField
            id="summary"
            name="summary"
            :placeholder="recruiter ? 'Résumez le profil en une phrase' : 'Résumez votre profil en une phrase'"
            :error="errors.summary"
          />
          <p class="mt-1 text-sm text-zinc-500" v-if="!recruiter">Rédigez une courte phrase pour résumer votre profil.</p>
        </div>

        <div>
          <label class="mb-1 block font-bold">Spécialisation &amp; compétences</label>
          <ApplySectorSubSectorSelect
            name="specialisation"
            :groups="refs.data.specialisationGroups"
            :value="prefill.specialisation"
            :error="errors.specialisation"
          />
        </div>

        <div class="space-y-2">
          <ApplySegmented
            name="official"
            :label="recruiter ? 'Fonctionnaire / lauréat de concours' : 'Je suis fonctionnaire'"
            :options="recruiter ? YES_NO_UNKNOWN : YES_NO_DECLINE"
            :value="prefill.official"
          />
          <FormField
            id="exam"
            v-if="values.official === true"
            custom
            name="exam"
            type="select"
            with-autocomplete
            placeholder="Nom du concours"
            :options="refs.data.exams"
            :value="prefill.exam"
            :error="errors.exam"
            @select="v => setFieldValue('exam', v)"
          />
        </div>

        <ApplySegmented name="experience" label="Niveau d'expérience" :options="EXPERIENCE_OPTIONS" :value="prefill.experience" />

        <FormField
          id="expertises"
          name="expertises"
          type="select"
          multiple
          with-autocomplete
          label="Diplômes, certifications &amp; expertises"
          placeholder="Rechercher…"
          :options="refs.data.expertises"
          :value="prefill.expertises"
          @select="v => setFieldValue('expertises', v)"
        />

        <FormField
          id="drivingLicenses"
          name="drivingLicenses"
          type="select"
          multiple
          with-autocomplete
          label="Permis"
          placeholder="Rechercher…"
          :options="refs.data.drivingLicenses"
          :value="prefill.drivingLicenses"
          @select="v => setFieldValue('drivingLicenses', v)"
        />

        <FormField
          id="languages"
          name="languages"
          type="select"
          multiple
          with-autocomplete
          label="Langues"
          placeholder="Rechercher…"
          :options="refs.data.languages"
          :value="prefill.languages"
          @select="v => setFieldValue('languages', v)"
        />

        <ApplySegmented
          v-if="askApplicationTags && !resumeDeposit"
          name="internalCandidate"
          label="Je travaille actuellement au sein de l'institution"
          :options="YES_NO"
        />

        <!-- Keep sensitive unanswered values distinct from “No”. -->
        <ApplySegmented
          v-if="askApplicationTags"
          name="rqth"
          label="Je bénéficie d'une RQTH ? (Reconnaissance Qualité de Travailleur Handicapé)"
          :options="YES_NO_DECLINE"
          :value="initialValues.rqth"
        />

        <slot name="extra" :errors="errors" :values="values" :set-field-value="setFieldValue" />

        <FormPolicyCheckbox
          v-if="!recruiter"
          :error="errors.policy"
          :career-site="careerSite"
          :link-color="theme.primary || '#58d9db'"
          :purpose="policyPurpose"
        />
      </div>

      <div class="mt-6 flex items-center justify-center gap-3">
        <Button color="secondary" type="button" :style="cancelButtonStyle" @click="emit('back')">Annuler</Button>
        <Button
          :color="theme.primary ? 'secondary' : 'primary'"
          :disabled="isSubmitting || props.submitting"
          type="submit"
          :class="theme.primary ? '!bg-white hover:!text-[color:var(--button-hover-color)]' : ''"
          :style="primaryButtonStyle"
          >{{ submitLabel }}</Button
        >
      </div>
    </Form>
  </div>
</template>
